import React, { useEffect, useMemo, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { ChevronRightIcon, ClockIcon } from '@heroicons/react/24/outline'
import { getArticle, categoryLabel, isDev } from '../knowledge/articles'
import { parseFrontmatter, extractHeadings, estimateReadingTime, MarkdownBody } from '../knowledge/Markdown'
import { buildComponentMap } from '../knowledge/ArticleBlocks'
import { ETYKIETY_TAGOW, tagiArtykulu } from '../knowledge/tagi'
import Loader from '../components/Loader'
import useDocumentTitle from '../hooks/useDocumentTitle'

const Crumb = ({ to, children, current }) =>
  current ? (
    <span className="text-gray-700 font-medium">{children}</span>
  ) : (
    <Link to={to} className="text-gray-400 hover:text-blue-600 transition-colors">{children}</Link>
  )

const Toc = ({ headings, activeId }) => (
  <nav aria-label="Spis treści">
    <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-3">Na tej stronie</p>
    <ul className="border-l border-gray-100">
      {headings.map((h) => (
        <li key={h.id}>
          <a
            href={`#${h.id}`}
            className={`block border-l-2 -ml-px py-1 text-sm transition-colors ${h.level === 3 ? 'pl-6' : 'pl-3'} ${
              activeId === h.id ? 'border-blue-500 text-blue-600 font-medium' : 'border-transparent text-gray-500 hover:text-gray-800'
            }`}
          >
            {h.text}
          </a>
        </li>
      ))}
    </ul>
  </nav>
)

const HeroIllustration = ({ hero, lead }) => (
  <div className="rounded-xl border border-gray-200 bg-gradient-to-r from-blue-50 to-white p-5 flex items-center justify-between gap-4">
    <div>
      <p className="text-xs uppercase tracking-wide text-blue-600 font-semibold">Poradnik techniczny</p>
      <p className="text-sm text-gray-600 mt-1">{hero?.subtitle || lead}</p>
    </div>
    {hero?.value && (
      <div className="hidden sm:flex items-end gap-1.5" aria-hidden="true">
        <span className="w-2.5 rounded-t bg-blue-500" style={{ height: 36 }} />
        <span className="w-2.5 rounded-t bg-blue-300" style={{ height: 22 }} />
        <span className="w-2.5 rounded-t bg-blue-300" style={{ height: 22 }} />
        <span className="ml-3 text-2xl font-extrabold text-blue-600 leading-none">{hero.value}<span className="text-base align-top ml-0.5">{hero.unit}</span></span>
      </div>
    )}
  </div>
)

const RelatedArticles = ({ items }) => {
  if (!items || items.length === 0) return null
  const resolved = items
    .map((it) => {
      if (typeof it === 'string') {
        const art = getArticle(it)
        if (art && (art.status === 'published' || isDev)) return { title: art.title, slug: art.slug }
        return null
      }
      return { title: it.title, planned: true }
    })
    .filter(Boolean)
  if (resolved.length === 0) return null
  return (
    <section className="mt-12 pt-8 border-t border-gray-100">
      <h2 className="text-lg font-bold text-gray-900 mb-4">Powiązane artykuły</h2>
      <div className="grid gap-3 sm:grid-cols-2">
        {resolved.map((a) =>
          a.slug ? (
            <Link
              key={a.slug}
              to={`/baza-wiedzy/${a.slug}`}
              className="rounded-lg border border-gray-200 bg-white p-4 hover:border-blue-200 hover:shadow-sm transition group"
            >
              <p className="text-sm font-semibold text-gray-800 group-hover:text-blue-700 leading-snug">{a.title}</p>
              <span className="mt-2 inline-flex items-center gap-1 text-xs text-blue-600">
                Czytaj artykuł <ChevronRightIcon className="w-3.5 h-3.5" />
              </span>
            </Link>
          ) : (
            <div key={a.title} className="rounded-lg border border-gray-100 bg-gray-50/60 p-4">
              <p className="text-sm font-medium text-gray-500">{a.title}</p>
              <span className="mt-2 inline-block text-[11px] uppercase tracking-wide text-gray-400">w przygotowaniu</span>
            </div>
          )
        )}
      </div>
    </section>
  )
}

const RelatedProducts = ({ data }) => {
  if (!data) return null
  return (
    <section className="mt-10">
      <h2 className="text-lg font-bold text-gray-900 mb-4">Powiązane produkty</h2>
      <div className="flex flex-wrap gap-2 mb-3">
        {data.items.map((it) => (
          <span key={it.label} className="rounded-md bg-gray-50 border border-gray-200 px-3 py-1.5 text-xs text-gray-400">
            {it.label} · wkrótce
          </span>
        ))}
      </div>
      {data.all?.to && (
        <Link to={data.all.to} className="inline-flex items-center gap-1.5 text-sm font-semibold text-blue-600 hover:underline">
          {data.all.label} <ChevronRightIcon className="w-4 h-4" />
        </Link>
      )}
    </section>
  )
}

const KnowledgeArticle = () => {
  const { slug } = useParams()
  const article = getArticle(slug)

  const [state, setState] = useState({ loading: true, error: false, data: {}, body: '' })
  const [activeId, setActiveId] = useState('')

  // Ten sam tytuł co w HTML z prerenderu (scripts/prerender-seo.js): krótki seoTitle do wyników
  // wyszukiwania, pełny tytuł zostaje w nagłówku H1.
  useDocumentTitle(article?.seoTitle || article?.title)

  useEffect(() => { window.scrollTo(0, 0) }, [slug])

  useEffect(() => {
    if (!article) { setState({ loading: false, error: true, data: {}, body: '' }); return }
    let cancelled = false
    setState((s) => ({ ...s, loading: true, error: false }))
    fetch(article.mdPath)
      .then((res) => { if (!res.ok) throw new Error('not found'); return res.text() })
      .then((raw) => { if (cancelled) return; const { data, body } = parseFrontmatter(raw); setState({ loading: false, error: false, data, body }) })
      .catch(() => { if (!cancelled) setState({ loading: false, error: true, data: {}, body: '' }) })
    return () => { cancelled = true }
  }, [article])

  const headings = useMemo(() => (state.body ? extractHeadings(state.body) : []), [state.body])
  const components = useMemo(() => buildComponentMap(), [])

  useEffect(() => {
    if (!headings.length) return undefined
    const observer = new IntersectionObserver(
      (entries) => { entries.forEach((e) => { if (e.isIntersecting) setActiveId(e.target.id) }) },
      { rootMargin: '-96px 0px -70% 0px', threshold: 0 }
    )
    headings.forEach((h) => { const el = document.getElementById(h.id); if (el) observer.observe(el) })
    return () => observer.disconnect()
  }, [headings])

  // Draft niepublikowany w produkcji => traktuj jak brak.
  if (article && article.status !== 'published' && !isDev) {
    return <div className="px-4 md:px-10 lg:px-24 mt-10 text-gray-500">Artykuł nie jest jeszcze dostępny.</div>
  }

  if (state.loading) {
    return <div className="px-4 md:px-10 lg:px-24 mt-10 flex justify-center py-20"><Loader /></div>
  }

  if (!article || state.error) {
    return (
      <div className="px-4 md:px-10 lg:px-24 mt-10 mb-16">
        <p className="text-lg text-gray-400 mb-4">Nie znaleziono artykułu.</p>
        <Link to="/baza-wiedzy" className="text-blue-600 hover:underline text-sm">← Wróć do Bazy wiedzy</Link>
      </div>
    )
  }

  const title = state.data.title || article.title
  const lead = article.lead || state.data.description
  const readingTime = Number(state.data.reading_time) || article.readingTime || estimateReadingTime(state.body)
  const updatedAt = state.data.updated_at || article.updatedAt
  const catLabel = categoryLabel(article.category)

  return (
    <div className="mx-auto max-w-[1160px] px-4 sm:px-6 lg:px-8 mt-6 mb-16">
      <div className="lg:grid lg:grid-cols-[minmax(0,760px)_minmax(220px,280px)] lg:gap-14 lg:justify-center">
        <article className="max-w-[760px] mx-auto lg:mx-0 min-w-0">
          {/* Breadcrumbs */}
          <nav aria-label="breadcrumb" className="flex items-center flex-wrap gap-1.5 text-sm mb-5">
            <Crumb to="/baza-wiedzy">Baza wiedzy</Crumb>
            <ChevronRightIcon className="w-4 h-4 text-gray-300" />
            <Crumb to={`/baza-wiedzy?category=${article.category}`}>{catLabel}</Crumb>
            {article.breadcrumbTopic && (
              <>
                <ChevronRightIcon className="w-4 h-4 text-gray-300" />
                <Crumb current>{article.breadcrumbTopic}</Crumb>
              </>
            )}
          </nav>

          <div className="flex items-center gap-2 mb-3">
            <span className="rounded-full bg-blue-50 text-blue-700 text-xs font-medium px-2.5 py-0.5">{catLabel}</span>
            <span className="rounded-full bg-gray-100 text-gray-500 text-xs font-medium px-2.5 py-0.5">Poradnik techniczny</span>
          </div>

          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 leading-tight">{title}</h1>
          {lead && <p className="mt-3 text-gray-500 text-base leading-7">{lead}</p>}

          <div className="mt-4 flex items-center gap-4 text-xs text-gray-400">
            <span className="inline-flex items-center gap-1.5"><ClockIcon className="w-4 h-4" /> {readingTime} min czytania</span>
            {updatedAt && <span>Aktualizacja: {updatedAt}</span>}
          </div>

          <div className="mt-6"><HeroIllustration hero={article.hero} lead={lead} /></div>

          {/* Mobilny spis treści */}
          {headings.length > 0 && (
            <details className="lg:hidden mt-6 rounded-lg border border-gray-200 bg-gray-50 p-4">
              <summary className="text-sm font-semibold text-gray-700 cursor-pointer">Spis treści</summary>
              <div className="mt-3"><Toc headings={headings} activeId={activeId} /></div>
            </details>
          )}

          <div className="mt-8">
            <MarkdownBody body={state.body} components={components} />
          </div>

          {tagiArtykulu(article.slug).length > 0 && (
            <div className="mt-8 flex flex-wrap items-center gap-1.5">
              <span className="text-sm text-gray-500 mr-1">Tagi:</span>
              {tagiArtykulu(article.slug).map((tag) => (
                <Link
                  key={tag}
                  to={`/baza-wiedzy?tagi=${tag}`}
                  className="rounded-full border border-gray-200 bg-white px-2.5 py-1 text-xs font-medium text-gray-600 hover:border-blue-300 hover:text-blue-600 transition-colors"
                >
                  {ETYKIETY_TAGOW[tag]}
                </Link>
              ))}
            </div>
          )}
          <RelatedArticles items={article.related} />
          <RelatedProducts data={article.relatedProducts} />

          <div className="mt-10">
            <Link to="/baza-wiedzy" className="text-sm text-blue-600 hover:underline">← Wróć do Bazy wiedzy</Link>
          </div>
        </article>

        <aside className="hidden lg:block">
          <div className="sticky top-24">
            <Toc headings={headings} activeId={activeId} />
          </div>
        </aside>
      </div>
    </div>
  )
}

export default KnowledgeArticle
